import localforage from 'localforage';

import { ECexName } from 'web3';

const STORE_NAME = 'order-book-snapshots';
const STORE_CONFIG = {
  name: STORE_NAME,
  storeName: 'snapshots',
};

// Chunk size: 1 minute = 60000ms
const CHUNK_DURATION_MS = 60_000;

// Retention period: 24 hours
const RETENTION_PERIOD_MS = 24 * 60 * 60 * 1000;

export interface OrderBookSnapshot {
  timestamp: number; // milliseconds
  bidPrice: number;
  bidQty: number;
  askPrice: number;
  askQty: number;
}

interface StoredChunk {
  chunkStart: number; // timestamp of chunk start (rounded down to minute)
  snapshots: OrderBookSnapshot[];
}

const storage = localforage.createInstance(STORE_CONFIG);

// Ensure write operations for the same chunk are executed sequentially to avoid race conditions
const writeQueues = new Map<string, Promise<void>>();

const enqueueWrite = (key: string, operation: () => Promise<void>): Promise<void> => {
  const current = writeQueues.get(key) ?? Promise.resolve();
  const next = current
    .catch(() => undefined)
    .then(operation)
    .finally(() => {
      if (writeQueues.get(key) === next) {
        writeQueues.delete(key);
      }
    });

  writeQueues.set(key, next);
  return next;
};

const ensureReady = async () => {
  try {
    await storage.ready();
  } catch (error) {
    console.error('[OrderBookStorage] IndexedDB not ready', error);
    throw error;
  }
};

/**
 * Build storage key for a specific exchange, symbol, and time chunk
 * Format: "ob:{exchange}:{symbol}:{chunkStart}"
 * Example: "ob:binance:BTCUSDT:1698768000000"
 */
const buildChunkKey = (exchange: ECexName, symbol: string, chunkStart: number): string =>
  `ob:${exchange}:${symbol}:${chunkStart}`;

/**
 * Get the chunk start timestamp for a given timestamp
 * Rounds down to the nearest minute
 */
const getChunkStart = (timestamp: number): number =>
  Math.floor(timestamp / CHUNK_DURATION_MS) * CHUNK_DURATION_MS;

/**
 * Build pattern for matching all chunks of a specific exchange and symbol
 * Format: "ob:{exchange}:{symbol}:"
 */
const buildKeyPattern = (exchange: ECexName, symbol: string): string => `ob:${exchange}:${symbol}:`;

/**
 * Save an order book snapshot to IndexedDB
 * Snapshots are grouped into 1-minute chunks to avoid huge records
 */
export const saveOrderBookSnapshot = async ({
  exchange,
  symbol,
  timestamp,
  bidPrice,
  bidQty,
  askPrice,
  askQty,
}: {
  exchange: ECexName;
  symbol: string;
  timestamp: number;
  bidPrice: number;
  bidQty: number;
  askPrice: number;
  askQty: number;
}): Promise<void> => {
  try {
    const chunkStart = getChunkStart(timestamp);
    const key = buildChunkKey(exchange, symbol, chunkStart);

    await enqueueWrite(key, async () => {
      await ensureReady();

      // Get existing chunk or create new one
      const existingChunk = await storage.getItem<StoredChunk>(key);

      const snapshot: OrderBookSnapshot = {
        timestamp,
        bidPrice,
        bidQty,
        askPrice,
        askQty,
      };

      if (existingChunk) {
        // Append to existing chunk and keep snapshots ordered by timestamp
        existingChunk.snapshots.push(snapshot);
        existingChunk.snapshots.sort((a, b) => a.timestamp - b.timestamp);
        await storage.setItem(key, existingChunk);
      } else {
        // Create new chunk
        const newChunk: StoredChunk = {
          chunkStart,
          snapshots: [snapshot],
        };
        await storage.setItem(key, newChunk);
      }
    });
  } catch (error) {
    console.error('[OrderBookStorage] Failed to save snapshot', {
      exchange,
      symbol,
      timestamp,
      error,
    });
  }
};

/**
 * Load order book snapshots for a specific time range
 * Returns snapshots sorted by timestamp
 */
export const loadOrderBookSnapshots = async ({
  exchange,
  symbol,
  startTs,
  endTs,
}: {
  exchange: ECexName;
  symbol: string;
  startTs: number;
  endTs: number;
}): Promise<OrderBookSnapshot[]> => {
  try {
    await ensureReady();

    const allSnapshots: OrderBookSnapshot[] = [];

    // Normalize range to ensure start <= end
    const rangeStart = Math.min(startTs, endTs);
    const rangeEnd = Math.max(startTs, endTs);

    // Calculate chunk boundaries
    const firstChunkStart = getChunkStart(rangeStart);
    const lastChunkStart = getChunkStart(rangeEnd);

    // Load all relevant chunks
    const chunkPromises: Promise<void>[] = [];

    for (
      let chunkStart = firstChunkStart;
      chunkStart <= lastChunkStart;
      chunkStart += CHUNK_DURATION_MS
    ) {
      const key = buildChunkKey(exchange, symbol, chunkStart);

      chunkPromises.push(
        storage.getItem<StoredChunk>(key).then(chunk => {
          if (chunk && chunk.snapshots) {
            // Filter snapshots within the requested time range
            const filtered = chunk.snapshots.filter(
              snap => snap.timestamp >= rangeStart && snap.timestamp <= rangeEnd,
            );
            allSnapshots.push(...filtered);
          }
        }),
      );
    }

    await Promise.all(chunkPromises);

    // Sort by timestamp
    allSnapshots.sort((a, b) => a.timestamp - b.timestamp);

    // Deduplicate snapshots with identical timestamps (keep first occurrence)
    const deduped: OrderBookSnapshot[] = [];
    let lastTimestamp: number | null = null;

    for (const snapshot of allSnapshots) {
      if (lastTimestamp === null || snapshot.timestamp !== lastTimestamp) {
        deduped.push(snapshot);
        lastTimestamp = snapshot.timestamp;
      }
    }

    return deduped;
  } catch (error) {
    console.error('[OrderBookStorage] Failed to load snapshots', {
      exchange,
      symbol,
      startTs,
      endTs,
      error,
    });
    return [];
  }
};

/**
 * Prune old snapshots to maintain retention period (24 hours)
 * Removes all chunks older than the specified timestamp
 */
export const pruneOldSnapshots = async ({
  exchange,
  symbol,
  beforeTs,
}: {
  exchange: ECexName;
  symbol: string;
  beforeTs: number;
}): Promise<void> => {
  try {
    await ensureReady();

    const keyPattern = buildKeyPattern(exchange, symbol);
    const allKeys = await storage.keys();

    // Find all keys matching this exchange/symbol
    const matchingKeys = allKeys.filter(key => key.startsWith(keyPattern));

    // Remove chunks older than beforeTs
    const removePromises: Promise<void>[] = [];

    for (const key of matchingKeys) {
      // Extract chunk start from key (last part after final ":")
      const parts = key.split(':');
      const chunkStart = parseInt(parts[parts.length - 1], 10);

      if (!Number.isNaN(chunkStart) && chunkStart < beforeTs) {
        removePromises.push(storage.removeItem(key));
      }
    }

    const removedCount = removePromises.length;
    await Promise.all(removePromises);

    if (removedCount > 0) {
      console.log(`[OrderBookStorage] Pruned ${removedCount} old chunks for ${exchange}:${symbol}`);
    }
  } catch (error) {
    console.error('[OrderBookStorage] Failed to prune snapshots', { exchange, symbol, error });
  }
};

/**
 * Get retention cutoff timestamp (now - 24 hours)
 */
export const getRetentionCutoff = (): number => Date.now() - RETENTION_PERIOD_MS;

/**
 * Clear all snapshots for a specific exchange and symbol
 * Useful for testing or manual cleanup
 */
export const clearAllSnapshots = async ({
  exchange,
  symbol,
}: {
  exchange: ECexName;
  symbol: string;
}): Promise<void> => {
  try {
    await ensureReady();

    const keyPattern = buildKeyPattern(exchange, symbol);
    const allKeys = await storage.keys();
    const matchingKeys = allKeys.filter(key => key.startsWith(keyPattern));

    await Promise.all(matchingKeys.map(key => storage.removeItem(key)));

    console.log(
      `[OrderBookStorage] Cleared ${matchingKeys.length} chunks for ${exchange}:${symbol}`,
    );
  } catch (error) {
    console.error('[OrderBookStorage] Failed to clear snapshots', { exchange, symbol, error });
  }
};
